// components/ProductSelector/ProductSelector.jsx
import React from 'react'
import AsyncSelector from 'components/AsyncSelector'
import HighlightableText from 'components/HighlightableText'
import { formatNumberForDisplay } from 'utils/numberUtils'
import { API_UPLOADS_URL } from 'config/general'
import './ProductSelector.css'

/**
 * Selector específico para productos con configuración predefinida
 * 
 * @param {object} value - Producto seleccionado actual
 * @param {function} onChange - Callback cuando cambia la selección
 * @param {boolean} disabled - Si está deshabilitado
 * @param {boolean} isClearable - Si se puede limpiar la selección
 * @param {string} placeholder - Texto placeholder
 * @param {object} additionalFilters - Filtros adicionales para la API
 * @param {boolean} showPrice - Si mostrar precio en el label
 * @param {boolean} showCode - Si mostrar código en el label
 * @param {boolean} showImage - Si mostrar imagen en las opciones
 * @param {object} customStyles - Estilos personalizados
 */
const ProductSelector = ({
	value = null,
	onChange = () => { },
	disabled = false,
	isClearable = true,
	placeholder = 'Buscar producto...',
	additionalFilters = { activo: true }, // Solo productos activos por defecto
	showPrice = true,
	showCode = true,
	showImage = true,
	customStyles = {},
	...otherProps
}) => {

	// Función para formatear el precio
	const formatearPrecio = (precio) => {
		if (!precio || precio === 0) return ""
		return `₲ ${formatNumberForDisplay(precio.toString())}`
	}

	// Función para formatear el label del producto
	const formatProductLabel = (product) => {
		if (!product) return ''

		let label = product.nombre || 'Producto sin nombre'

		// Agregar código si está disponible y se solicita
		if (showCode && product.codigo) {
			label += ` (${product.codigo})`
		}

		// Agregar precio si está disponible y se solicita
		if (showPrice && product.precio) {
			const precio = formatearPrecio(product.precio)
			if (precio) {
				label += ` - ${precio}`
			}
		}

		return label
	}

	// Función para formatear cómo se ve cada opción en el dropdown
	const formatProductOption = (option, { context }) => {
		if (!option || !option.data) return null

		const product = option.data
		const searchTerm = option.searchTerm || ''

		return (
			<div className="product-option">
				<div className="d-flex align-items-center">
					{/* Imagen del producto */}
					{showImage && (
						<div className="product-option-image me-3">
							{product.multimedia && product.multimedia.length > 0 ? (
								<img
									src={API_UPLOADS_URL + product.multimedia[0].url}
									alt={product.nombre}
									className="product-thumb"
									onError={(e) => {
										e.target.style.display = 'none'
									}}
								/>
							) : (
								<div className="product-thumb-placeholder">
									<i className="fa fa-image text-muted"></i>
								</div>
							)}
						</div>
					)}

					{/* Información principal */}
					<div className="product-option-info flex-grow-1">
						<div className="product-option-main">
							<HighlightableText
								text={product.nombre || 'Sin nombre'}
								highlight={searchTerm}
								className="product-option-name"
							/>

							{showCode && product.codigo && (
								<span className="product-option-code ms-2 text-muted">
									(<HighlightableText
										text={product.codigo}
										highlight={searchTerm}
										highlightClassName="highlighted-text subtle"
									/>)
								</span>
							)}
						</div>

						{/* Información adicional en segunda línea */}
						<div className="product-option-meta">
							{showPrice && product.precio && (
								<small className="text-success fw-bold">
									{formatearPrecio(product.precio)}
								</small>
							)}

							{product.precioPromocion && (
								<small className="text-danger fw-bold ms-2">
									<i className="fa fa-tag me-1"></i>
									{formatearPrecio(product.precioPromocion)}
								</small>
							)}

							{/* Indicar si está inactivo */}
							{product.activo === false && (
								<small className="text-warning ms-2">
									<i className="fa fa-exclamation-triangle me-1"></i>
									Inactivo
								</small>
							)}
						</div>
					</div>
				</div>
			</div>
		)
	}

	// Estilos específicos para el selector de productos
	const productStyles = {
		option: (provided, state) => ({
			...provided,
			padding: '12px',
			backgroundColor: state.isFocused ? '#f8f9fa' : 'white',
			borderBottom: '1px solid #f0f0f0',
			cursor: 'pointer',
			'&:hover': {
				backgroundColor: '#e9ecef'
			}
		}),
		...customStyles
	}

	return (
		<AsyncSelector
			endpoint="a/ver-items"
			searchFields={['busqueda']}
			valueField="id"
			labelFormatter={formatProductLabel}
			optionFormatter={formatProductOption}
			placeholder={placeholder}
			value={value}
			onChange={onChange}
			disabled={disabled}
			isClearable={isClearable}
			additionalFilters={additionalFilters}
			minSearchLength={1}
			maxResults={15}
			noOptionsMessage="No se encontraron productos"
			loadingMessage="Buscando productos..."
			customStyles={productStyles}
			{...otherProps}
		/>
	)
}

export default ProductSelector